import { parseCustomizationDefinition } from './customizationDefinition';

/**
 * Helpers for the supplyOrderIntegration Customization definition JSON (see
 * SupplyOrderIntegrationDefinition.cs): the assistant envelope
 * { profile: 'supplyOrderIntegration', typeVersion, outputs: [{ name, script }], queries: [{ name, query }],
 *   samples: [{ name, source, status, headers, body }], testRecord, notes, ... }
 * with exactly five known outputs (OUTPUTS). The server stamps typeVersion at publish; everything else
 * is author-owned and preserved byte-for-byte in value. Data store rows (secrets included) are REQUESTED by the
 * dataStore output at run time and read as ctx.dataStore["scope:key"]: the values live in the data store (encrypted
 * rows read as an opaque $secret{scope:key} token) and never enter a definition.
 */

export const SUPPLY_ORDER_INTEGRATION_PROFILE = 'supplyOrderIntegration';

/** The five outputs, in the order the runtime runs them (dataStore -> transports -> credentials -> dispatch -> orderResponse). */
export const OUTPUT_DATA_STORE = 'dataStore';
export const OUTPUT_TRANSPORTS = 'transports';
export const OUTPUT_CREDENTIALS = 'credentials';
export const OUTPUT_DISPATCH = 'dispatch';
export const OUTPUT_ORDER_RESPONSE = 'orderResponse';
export const OUTPUTS = [OUTPUT_DATA_STORE, OUTPUT_TRANSPORTS, OUTPUT_CREDENTIALS, OUTPUT_DISPATCH, OUTPUT_ORDER_RESPONSE];

/** The host lanes the session authors beside the scripts (keys = server lane keys, labels = fenced block labels). */
export const SAMPLES_LANE = 'supplyOrderSamples';
export const FIELD_MAP_LANE = 'supplyOrderFieldMap';
export const MAPPING_PLAN_LANE = 'supplyOrderMappingPlan';
export const PRODUCT_MAPPINGS_LANE = 'supplyOrderProductMappings';
export const HOST_LANES = [
  { key: SAMPLES_LANE, label: 'artifact: samples', member: 'samples' },
  { key: FIELD_MAP_LANE, label: 'artifact: fieldMap', member: 'fieldMap' },
  { key: MAPPING_PLAN_LANE, label: 'artifact: mappingPlan', member: 'mappingPlan' },
  { key: PRODUCT_MAPPINGS_LANE, label: 'artifact: productMappings', member: 'productMappings' },
];

/** Values of mappingPlan.approach (SupplyOrderIntegrationMappingPlan.Approaches) with the operator-facing wording. */
export const MAPPING_PLAN_APPROACHES = [
  { id: 'supplierMappings', label: 'Their codes typed on each product, sent as typed' },
  { id: 'convention', label: 'Their codes typed on each product; an option\'s code = the field it fills' },
  { id: 'internal', label: 'Code list kept inside the integration' },
  { id: 'mixed', label: 'Their codes typed on each product; measurements and layout by the integration' },
];

/** mappingPlan.source of the provisional plan the editor seeds from the brief (SupplyOrderIntegrationMappingPlan.Sources.Brief). */
export const MAPPING_PLAN_SOURCE_BRIEF = 'brief';

/** Row statuses of the field map (SupplyOrderIntegrationFieldMap.Statuses). */
export const FIELD_MAP_STATUSES = ['mapped', 'derived', 'constant', 'unmapped'];

const safeString = (v) => (v ?? '').toString();
const lower = (v) => safeString(v).trim().toLowerCase();

/** The known outputs of a definition as [{ name, script }] in OUTPUTS order (missing ones have script ''). */
export const readOutputs = (definition) => {
  const def = parseCustomizationDefinition(definition);
  const list = Array.isArray(def?.outputs) ? def.outputs : [];
  return OUTPUTS.map((name) => {
    const found = list.find((o) => lower(o?.name) === name.toLowerCase());
    return { name, script: safeString(found?.script) };
  });
};

/** One output's script ('' when absent). */
export const readOutputScript = (definition, name) => readOutputs(definition).find((o) => o.name === name)?.script ?? '';

/** The queries[] normalised to [{ name, query }] (blank entries dropped). */
export const readQueries = (definition) => {
  const def = parseCustomizationDefinition(definition);
  return (Array.isArray(def?.queries) ? def.queries : [])
    .filter((q) => q && typeof q === 'object')
    .map((q) => ({ name: safeString(q.name ?? q.Name).trim(), query: safeString(q.query ?? q.sql ?? q.Query ?? q.Sql) }))
    .filter((q) => q.name && q.query.trim());
};

/** The samples[] normalised to [{ name, source, status, headers, body }]. */
export const readSamples = (definition) => {
  const def = parseCustomizationDefinition(definition);
  return (Array.isArray(def?.samples) ? def.samples : [])
    .filter((s) => s && typeof s === 'object')
    .map((s) => ({
      name: safeString(s.name ?? s.Name).trim(),
      source: safeString(s.source ?? s.Source).trim() || 'documentation',
      status: Number(s.status ?? s.Status) || 0,
      headers: s.headers && typeof s.headers === 'object' ? s.headers : {},
      body: typeof s.body === 'string' ? s.body : (s.body == null ? '' : JSON.stringify(s.body)),
    }));
};

/** The declared test supply job (0 when none). */
export const readTestRecordID = (definition) => {
  const def = parseCustomizationDefinition(definition);
  return Number(def?.testRecord) || 0;
};

/**
 * The supplier (Supplier.ID) the integration is designed for - chosen in the brief; whose products the Products tab lists,
 * whose supply jobs the test-record picker offers, whose facts the assistant sees (0 when none). Design-time only: a
 * supplier links to the integration from its own screen (InterfaceKey), never from here.
 */
export const readSupplierID = (definition) => {
  const def = parseCustomizationDefinition(definition);
  return Number(def?.supplierID) || 0;
};

/** The notes member ('' when none). */
export const readNotes = (definition) => safeString(parseCustomizationDefinition(definition)?.notes);

/**
 * The sheetscript builder API version (the `S.*` surface the dispatch output's workbook files are written against), or null
 * when the definition carries none (= the first version). The editor stamps the latest whenever the assistant writes.
 */
export const readSheetScriptApiVersion = (definition) => {
  const v = parseCustomizationDefinition(definition)?.sheetScriptApiVersion;
  return typeof v === 'string' && v.trim() ? v.trim() : null;
};

/** The dependent assets a definition declares ([{ key, purpose, designedFromHash }]; CustomizationDependentAssets.Member). */
export const readDependentAssets = (definition) => {
  const list = parseCustomizationDefinition(definition)?.dependentAssets;
  return Array.isArray(list) ? list : [];
};

/**
 * The field map ({ dispatchHash, rows: [{ section, target, status, source, rule, table }] }) a definition or a lane
 * artifact declares, normalised; null when absent / unusable. Accepts the stored object, a bare rows array, or JSON text.
 */
export const parseFieldMap = (input) => {
  let node = input;
  if (typeof node === 'string') {
    const text = node.trim();
    if (!text) return null;
    try { node = JSON.parse(text); } catch { return null; }
  }
  if (!node || typeof node !== 'object') return null;
  const rowsRaw = Array.isArray(node) ? node : (Array.isArray(node.rows) ? node.rows : (Array.isArray(node.Rows) ? node.Rows : null));
  if (!rowsRaw) return null;
  const rows = rowsRaw
    .filter((r) => r && typeof r === 'object')
    .map((r) => ({
      section: safeString(r.section ?? r.Section).trim(),
      target: safeString(r.target ?? r.Target).trim(),
      status: lower(r.status ?? r.Status),
      source: safeString(r.source ?? r.Source).trim(),
      rule: safeString(r.rule ?? r.Rule).trim(),
      table: safeString(r.table ?? r.Table).trim(),
    }));
  // the layout decision (whose columns a workbook copying a design reference has) rides with the rows: the server reads it
  // back from the lane artifact every turn, so dropping it here would have the assistant ask the user again
  const layoutRaw = Array.isArray(node) ? null : (node.layout ?? node.Layout);
  const acceptedRaw = layoutRaw && typeof layoutRaw === 'object' ? (layoutRaw.accepted ?? layoutRaw.Accepted) : null;
  const accepted = Array.isArray(acceptedRaw) ? acceptedRaw.map((a) => safeString(a).trim()).filter(Boolean) : [];
  const layout = layoutRaw && typeof layoutRaw === 'object'
    ? {
      reference: safeString(layoutRaw.reference ?? layoutRaw.Reference).trim(),
      columns: safeString(layoutRaw.columns ?? layoutRaw.Columns).trim(),
      ...(accepted.length > 0 ? { accepted } : {}),
    }
    : null;
  return {
    dispatchHash: Array.isArray(node) ? '' : lower(node.dispatchHash ?? node.DispatchHash),
    rows,
    ...(layout && (layout.reference || layout.columns || layout.accepted) ? { layout } : {}),
  };
};

/** The definition's field map, or null. */
export const readFieldMap = (definition) => parseFieldMap(parseCustomizationDefinition(definition)?.fieldMap);

/**
 * The value chain (SupplyOrderIntegrationMappingPlan.ValueSources): the ordered fallbacks the file walks for an option's
 * chosen value - the first that has something is sent. Each is something the user can see or type on the product.
 */
export const VALUE_SOURCES = [
  { id: 'theirCode', label: 'their code typed on the value' },
  { id: 'theirName', label: 'their name typed on the value' },
  { id: 'yourCode', label: 'your value\'s code' },
  { id: 'yourText', label: 'your value\'s text' },
];
/** The shorthand rules (SupplyOrderIntegrationMappingPlan.ValueRules) and the chains they stand for; 'chain' = an explicit list matching none. */
const VALUE_RULE_CHAINS = {
  none: [],
  valueCode: ['theirCode'],
  valueCodeElseValue: ['theirCode', 'yourText'],
  valueElseOurs: ['theirName', 'yourText'],
  valueCodeElseYourCode: ['theirCode', 'yourCode'],
};
export const valueChainOf = (rule) => VALUE_RULE_CHAINS[safeString(rule).trim()] ?? [];
export const valueRuleOf = (chain) => {
  const c = (Array.isArray(chain) ? chain : []).map((s) => safeString(s).trim()).filter(Boolean);
  if (c.length === 0) return 'none';
  const match = Object.entries(VALUE_RULE_CHAINS).find(([, v]) => v.length === c.length && v.every((s, i) => s.toLowerCase() === c[i].toLowerCase()));
  return match ? match[0] : 'chain';
};
/** The chain in the user's words: "their code typed on the value, else your value's text" (`translate` = the caller's t). */
export const describeValueChain = (chain, translate = (s) => s) => {
  const c = Array.isArray(chain) ? chain : [];
  if (c.length === 0) return translate('nothing from the value');
  return c.map((s) => translate(VALUE_SOURCES.find((v) => v.id === s)?.label ?? s)).join(`, ${translate('else')} `);
};

/**
 * The mapping plan ({ approach, summary, product: { uses, rule }, options: { uses, codeRule, fields, valueRule, valueSources, rule },
 * dispatchOwned, tables, operatorNotes } - SupplyOrderIntegrationMappingPlan) a definition or a lane artifact declares,
 * normalised; null when absent / unusable. Accepts the object, a definition-shaped wrapper or JSON text.
 */
export const parseMappingPlan = (input) => {
  let node = input;
  if (typeof node === 'string') {
    const text = node.trim();
    if (!text) return null;
    try { node = JSON.parse(text); } catch { return null; }
  }
  if (!node || typeof node !== 'object' || Array.isArray(node)) return null;
  if (node.mappingPlan && typeof node.mappingPlan === 'object') node = node.mappingPlan;
  const approach = lower(node.approach ?? node.Approach);
  if (!approach) return null;
  const strings = (v) => (Array.isArray(v) ? v.map((x) => safeString(x).trim()).filter(Boolean) : []);
  const product = node.product && typeof node.product === 'object' ? node.product : {};
  const options = node.options && typeof node.options === 'object' ? node.options : {};
  // the value chain: an explicit valueSources array is the truth (the rule follows it); otherwise the rule's shorthand expands
  const sourcesNode = options.valueSources ?? options.ValueSources;
  const ruleText = safeString(options.valueRule ?? options.ValueRule).trim() || 'none';
  const valueSources = Array.isArray(sourcesNode) ? strings(sourcesNode) : valueChainOf(ruleText);
  const valueRule = Array.isArray(sourcesNode) ? valueRuleOf(valueSources) : ruleText;
  // the supplier's closed value lists per field ({ field: [codes] }); a field absent here accepts any value code
  const fieldValuesNode = options.fieldValues ?? options.FieldValues;
  const fieldValues = {};
  if (fieldValuesNode && typeof fieldValuesNode === 'object' && !Array.isArray(fieldValuesNode)) {
    Object.keys(fieldValuesNode).forEach((key) => {
      const field = safeString(key).trim();
      const values = strings(fieldValuesNode[key]);
      if (field && values.length > 0 && !(field in fieldValues)) fieldValues[field] = values;
    });
  }
  return {
    approach,
    summary: safeString(node.summary ?? node.Summary).trim(),
    product: { uses: safeString(product.uses ?? product.Uses).trim() || 'none', rule: safeString(product.rule ?? product.Rule).trim() },
    options: {
      uses: safeString(options.uses ?? options.Uses).trim() || 'none',
      codeRule: safeString(options.codeRule ?? options.CodeRule).trim() || 'asIs',
      fields: strings(options.fields ?? options.Fields),
      ...(Object.keys(fieldValues).length > 0 ? { fieldValues } : {}),
      valueRule,
      valueSources,
      rule: safeString(options.rule ?? options.Rule).trim(),
    },
    dispatchOwned: strings(node.dispatchOwned ?? node.DispatchOwned),
    tables: strings(node.tables ?? node.Tables),
    operatorNotes: safeString(node.operatorNotes ?? node.OperatorNotes).trim(),
    // 'brief' marks the provisional plan seeded from the brief (SupplyOrderIntegrationMappingPlan.Sources); absent otherwise
    ...(lower(node.source ?? node.Source) === MAPPING_PLAN_SOURCE_BRIEF ? { source: MAPPING_PLAN_SOURCE_BRIEF } : {}),
  };
};

/** The definition's mapping plan, or null. */
export const readMappingPlan = (definition) => parseMappingPlan(parseCustomizationDefinition(definition)?.mappingPlan);

/** True for the plan seeded from the brief: the approach is settled, the details are defaults until the design chat confirms them. */
export const isProvisionalMappingPlan = (plan) => parseMappingPlan(plan)?.source === MAPPING_PLAN_SOURCE_BRIEF;

/**
 * The provisional mapping plan for an approach the brief states (anything but 'recommend'), so the Products tab can check
 * every product the supplier supplies before the design chat has refined the plan. The details are the natural reading
 * of each approach - the product's mapped Code, each option's mapped Code and the value's mapped Code else our value -
 * marked source 'brief'; the assistant's own plan replaces it, and a provisional plan does not publish. Null for
 * 'recommend' / blank / unknown.
 */
export const provisionalMappingPlanFromBrief = (approach) => {
  const id = lower(approach);
  if (!MAPPING_PLAN_APPROACHES.some((a) => a.id === id)) return null;
  const readsMappings = id !== 'internal';
  const summary = {
    supplierMappings: 'From the brief: their codes are typed on each of your products (the product\'s Supplier Mappings tab) and the order file sends them exactly as typed. Provisional until the design chat confirms the details.',
    convention: 'From the brief: their codes are typed on each of your products by a fixed rule - the code on an option is the name of the field it fills in their file. Which fields those are is settled in the design chat; until then the codes are checked as typed. Provisional.',
    internal: 'From the brief: a code list kept inside the integration; nothing is typed on your products. Provisional until the design chat confirms the details.',
    mixed: 'From the brief: their product, option and value codes are typed on each of your products; measurements, layout and the fields only some products carry are worked out by the integration. Provisional until the design chat confirms the details.',
  }[id];
  return parseMappingPlan({
    approach: id,
    summary,
    product: { uses: readsMappings ? 'code' : 'none', rule: '' },
    options: { uses: readsMappings ? 'codeAndValue' : 'none', codeRule: 'asIs', fields: [], valueSources: readsMappings ? ['theirCode', 'yourText'] : [], rule: '' },
    dispatchOwned: [],
    tables: [],
    operatorNotes: readsMappings ? 'For every one of your products they supply, open the product\'s Supplier Mappings tab and type their code for your product, for each option and, where they have codes for values, for each option value. The Products tab shows what is still missing per product.' : '',
    source: MAPPING_PLAN_SOURCE_BRIEF,
  });
};

const lowerGuid = (v) => lower(v).replace(/[{}]/g, '');
const isGuid = (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(lowerGuid(v));

/**
 * One product's worksheet entry ({ productID, product: { theirCode, theirName }, options: [ { option, id, theirCode, theirName,
 * values: [ { value, id, theirCode, theirName } ] } ] } - SupplyOrderIntegrationProductMappings) normalised: product ID lower-case,
 * codes trimmed, entries that name no node dropped. `option` / `value` are OUR codes; `id` the node's GUID. An option with
 * `ignore: true` is the decision that the supplier's file never needs it (no code, no values; the export clears what is typed).
 * Null when unusable.
 */
export const parseProductMappingsDraft = (input, productID) => {
  let node = input;
  if (typeof node === 'string') {
    const text = node.trim();
    if (!text) return null;
    try { node = JSON.parse(text); } catch { return null; }
  }
  if (!node || typeof node !== 'object' || Array.isArray(node)) return null;
  const id = lowerGuid(node.productID ?? node.ProductID ?? productID);
  if (!isGuid(id)) return null;
  const text = (v) => safeString(v).trim();
  const guid = (v) => (isGuid(v) ? lowerGuid(v) : '');
  const product = node.product && typeof node.product === 'object' ? node.product : {};
  const options = (Array.isArray(node.options) ? node.options : [])
    .filter((o) => o && typeof o === 'object')
    .map((o) => {
      const ignore = (o.ignore ?? o.Ignore) === true || lower(o.ignore ?? o.Ignore) === 'true';
      return {
        option: text(o.option ?? o.Option),
        id: guid(o.id ?? o.ID),
        ignore,
        // an ignored option carries nothing else
        theirCode: ignore ? '' : text(o.theirCode ?? o.TheirCode),
        theirName: ignore ? '' : text(o.theirName ?? o.TheirName),
        values: ignore ? [] : (Array.isArray(o.values) ? o.values : [])
          .filter((v) => v && typeof v === 'object')
          .map((v) => ({ value: text(v.value ?? v.Value), id: guid(v.id ?? v.ID), theirCode: text(v.theirCode ?? v.TheirCode), theirName: text(v.theirName ?? v.TheirName) }))
          .filter((v) => (v.value || v.id) && (v.theirCode || v.theirName)),
      };
    })
    .filter((o) => (o.option || o.id) && (o.ignore || o.theirCode || o.theirName || o.values.length > 0));
  return {
    productID: id,
    product: { theirCode: text(product.theirCode ?? product.TheirCode), theirName: text(product.theirName ?? product.TheirName) },
    options,
  };
};

/** True when the entry suggests nothing at all (the product's entry is then dropped from the worksheet). */
export const isEmptyProductMappingsDraft = (draft) => !draft || (!draft.product?.theirCode && !draft.product?.theirName && (draft.options ?? []).length === 0);

/**
 * The whole worksheet ({ '<productID>': draft }) a definition or a lane artifact declares, normalised; {} when none. Accepts the
 * object, a definition-shaped wrapper, a single draft or JSON text.
 */
export const parseProductMappings = (input) => {
  let node = input;
  if (typeof node === 'string') {
    const text = node.trim();
    if (!text) return {};
    try { node = JSON.parse(text); } catch { return {}; }
  }
  if (!node || typeof node !== 'object' || Array.isArray(node)) return {};
  if (node.productMappings && typeof node.productMappings === 'object' && !Array.isArray(node.productMappings)) node = node.productMappings;
  const result = {};
  if ((node.productID || node.ProductID) && Array.isArray(node.options ?? node.Options)) {
    const single = parseProductMappingsDraft(node);
    if (single && !isEmptyProductMappingsDraft(single)) result[single.productID] = single;
    return result;
  }
  Object.keys(node).forEach((key) => {
    const draft = parseProductMappingsDraft(node[key], key);
    if (draft && !isEmptyProductMappingsDraft(draft)) result[draft.productID] = draft;
  });
  return result;
};

/** The definition's worksheet ({ '<productID>': draft }), {} when none. */
export const readProductMappings = (definition) => parseProductMappings(parseCustomizationDefinition(definition)?.productMappings);

/** The worksheet with one product's entry replaced (an empty draft removes it); a new object, products in ID order. */
export const withProductMappingsDraft = (all, productID, draft) => {
  const id = lowerGuid(productID);
  const next = { ...(all ?? {}) };
  if (isEmptyProductMappingsDraft(draft)) delete next[id];
  else next[id] = { ...draft, productID: id };
  return Object.fromEntries(Object.keys(next).sort().map((k) => [k, next[k]]));
};

/** The lane artifacts the assistant dialog round-trips, read from a definition: { supplyOrderSamples, supplyOrderFieldMap, supplyOrderMappingPlan, supplyOrderProductMappings } (JSON each). */
export const readLaneArtifacts = (definition) => ({
  [SAMPLES_LANE]: JSON.stringify(readSamples(definition)),
  [FIELD_MAP_LANE]: JSON.stringify(readFieldMap(definition) ?? { dispatchHash: '', rows: [] }),
  [MAPPING_PLAN_LANE]: JSON.stringify(readMappingPlan(definition) ?? null),
  [PRODUCT_MAPPINGS_LANE]: JSON.stringify(readProductMappings(definition)),
});

/**
 * The brief the editor's wizard collected ({ shape, files, destination, auth, orderFile, reply, details, openQuestions }
 * - see SupplyOrderIntegrationBriefWizardDlg), or null when the definition has none. Author-owned: the server mirrors
 * it to the assistant as briefFacts and never reads it at run time.
 */
export const readBrief = (definition) => {
  const brief = parseCustomizationDefinition(definition)?.brief;
  return brief && typeof brief === 'object' && !Array.isArray(brief) ? brief : null;
};

const parseArray = (text) => {
  if (Array.isArray(text)) return text;
  const trimmed = safeString(text).trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Builds the definition to store/preview from the editor's fields, carrying over every other member of the existing
 * definition untouched. Each field replaces its member only when given (undefined leaves the member as it is):
 *   outputs       [{ name, script }]  - replaces the five known outputs (unknown names in the base are dropped)
 *   queries       [{ name, query }]
 *   samples       array or JSON text
 *   testRecord    number (0 drops the member)
 *   supplierID    number - the supplier the integration is designed for (0 drops the member)
 *   notes         string ('' drops the member)
 *   typeVersion   string - stamps the contract version the scripts are written for
 *   sheetScriptApiVersion string - stamps the sheetscript builder (S.*) version the dispatch's workbook files are written for
 *   dependentAssets [{ key, purpose, designedFromHash }] - the library assets the design is made from (empty drops the member)
 *   fieldMap      { dispatchHash, rows } or JSON text - the field-mapping table (null / no rows drops the member)
 *   mappingPlan   object or JSON text - the mapping plan (null / unusable drops the member)
 *   productMappings object or JSON text - the product mappings worksheet (empty drops the member)
 *   brief         object - the wizard's brief (null / empty drops the member)
 */
export const buildSupplyOrderIntegrationDefinition = ({ existing, outputs, queries, samples, testRecord, supplierID, notes, typeVersion, sheetScriptApiVersion, dependentAssets, fieldMap, mappingPlan, productMappings, brief }) => {
  const base = parseCustomizationDefinition(existing) ?? {};
  const def = { ...base, profile: SUPPLY_ORDER_INTEGRATION_PROFILE };
  // the retired secrets[] declarations (rows are requested by the dataStore output now) are dropped from older drafts
  delete def.secrets;

  if (Array.isArray(outputs)) {
    const byName = new Map(outputs.map((o) => [lower(o?.name), safeString(o?.script)]));
    // an output with a blank script is simply absent (the runtime reports a missing required output by name)
    def.outputs = OUTPUTS
      .map((name) => ({ name, script: byName.get(name.toLowerCase()) ?? '' }))
      .filter((o) => o.script.trim() !== '');
  } else if (!Array.isArray(def.outputs)) {
    def.outputs = [];
  }

  if (queries !== undefined) {
    def.queries = (Array.isArray(queries) ? queries : [])
      .map((q) => ({ name: safeString(q?.name).trim(), query: safeString(q?.query ?? q?.sql) }))
      .filter((q) => q.name && q.query.trim());
  }
  if (samples !== undefined) def.samples = parseArray(samples);
  if (testRecord !== undefined) {
    if (Number(testRecord) > 0) def.testRecord = Number(testRecord);
    else delete def.testRecord;
  }
  if (supplierID !== undefined) {
    if (Number(supplierID) > 0) def.supplierID = Number(supplierID);
    else delete def.supplierID;
  }
  if (notes !== undefined) {
    if (safeString(notes).trim()) def.notes = safeString(notes);
    else delete def.notes;
  }
  if (typeof typeVersion === 'string' && typeVersion.trim()) def.typeVersion = typeVersion.trim();
  if (typeof sheetScriptApiVersion === 'string' && sheetScriptApiVersion.trim()) def.sheetScriptApiVersion = sheetScriptApiVersion.trim();
  if (dependentAssets !== undefined) {
    const list = Array.isArray(dependentAssets) ? dependentAssets : [];
    if (list.length > 0) def.dependentAssets = list;
    else delete def.dependentAssets;
  }
  if (fieldMap !== undefined) {
    const map = parseFieldMap(fieldMap);
    if (map && map.rows.length > 0) def.fieldMap = map;
    else delete def.fieldMap;
  }
  if (mappingPlan !== undefined) {
    const plan = parseMappingPlan(mappingPlan);
    if (plan) def.mappingPlan = plan;
    else delete def.mappingPlan;
  }
  if (productMappings !== undefined) {
    const worksheet = parseProductMappings(productMappings);
    if (Object.keys(worksheet).length > 0) def.productMappings = worksheet;
    else delete def.productMappings;
  }
  if (brief !== undefined) {
    if (brief && typeof brief === 'object' && !Array.isArray(brief) && Object.keys(brief).length > 0) def.brief = brief;
    else delete def.brief;
  }

  return JSON.stringify(def);
};

/**
 * The definition the assistant receives as its host context base: everything the session does NOT author
 * (outputs, queries, samples, the field map, the mapping plan and the product mappings worksheet removed; the retired
 * secrets[] too), so the server splices the session's copies back in.
 */
export const stripSessionArtifacts = (definition) => {
  const base = parseCustomizationDefinition(definition) ?? {};
  const { outputs, queries, secrets, samples, fieldMap, mappingPlan, productMappings, ...rest } = base;
  return { ...rest, profile: SUPPLY_ORDER_INTEGRATION_PROFILE };
};
